import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import robotAnimation from '../../../../shared/ui/AnimatedIcon/animations/robot.json';
import { usePrediction } from '../../hooks/usePrediction';
import { useTypewriter } from '../../hooks/useTypewriter';

import styles from './PredictionBubble.module.css';

const BOT_ENTRANCE_DELAY_MS = 500;
const TYPE_DELAY_MS = 320;

/**
 * SWRN-1033 — ProstoBot delivers a post-purchase prediction (instead of ads).
 * The bot enters with a delay and types the prediction right away — no user
 * interaction required (the tap-to-reveal step was removed in SWRN-1718).
 * Auto-hides when the loyalty program is active (handled by usePrediction).
 */
export const PredictionBubble = () => {
    const { t } = useTranslation();
    const { shouldShow, text } = usePrediction();

    const [isBotVisible, setIsBotVisible] = useState(false);

    const { displayed, isTyping } = useTypewriter(text, {
        enabled: isBotVisible,
        startDelay: TYPE_DELAY_MS,
    });

    useEffect(() => {
        const timeoutId = setTimeout(
            () => setIsBotVisible(true),
            BOT_ENTRANCE_DELAY_MS,
        );
        return () => clearTimeout(timeoutId);
    }, []);

    if (!shouldShow) {
        return null;
    }

    const mascotClassName = [
        styles.mascotWrapper,
        isBotVisible && styles.mascotVisible,
    ]
        .filter(Boolean)
        .join(' ');

    const bubbleClassName = [
        styles.bubble,
        isBotVisible && styles.bubbleVisible,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={styles.wrapper}>
            <div className={mascotClassName}>
                <AnimatedIcon
                    animationData={robotAnimation}
                    className={styles.mascot}
                />
            </div>

            <div className={bubbleClassName}>
                <p className={styles.label}>{t('prediction.label')}</p>
                <p className={styles.text}>
                    {displayed}
                    {isTyping && <span className={styles.caret} aria-hidden="true" />}
                </p>
            </div>
        </div>
    );
};
